// Full-document render of the toppers HomePage: PageSkeleton (the React
// document shell) wrapping the ``home_page.html`` hero DOM around the
// three existing HomePage islands (email signup, hero carousel, body props).
//
// The hero keeps most of its chrome server-rendered here: the ALWAYS-rendered
// mobile background div, the optional promo ribbon, the ``purchase_info``-driven
// price copy (4 variants), the ``|richtext`` offer, the two DIFFERENT SHOP NOW
// links (desktop = ``slugurl 'tempur-vs-competitors'``, mobile = the pdp url),
// and the ``#scroll-trigger`` GSAP pin anchor. The price-display strings, the
// promo image ``<img>`` tag, and the ``richtext``/``|safe`` HTML are all resolved
// server-side (renditions / oscar pricing / slugurl are server-only) and passed
// as scalar side-channels; the ``|safe`` promo copy and ``|richtext`` offer are
// injected raw via dangerouslySetInnerHTML.
//
// The ``#scroll-trigger`` container hosts a GSAP "wipe" ScrollTrigger, set up
// post-hydration in a useEffect below (dynamic-imported so GSAP never loads
// under SSR); it wraps the container in a pin-spacer, so the subtree carries no
// other React handlers.
import React, { useEffect, useRef } from "react";

import { templates } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { HomePageBody } from "../components/HomePageBody";
import { HomePageEmailSignup } from "../components/HomePageEmailSignup";
import { HomePageHeroCarousel } from "../components/HomePageHeroCarousel";
import { PageSkeleton } from "../components/PageSkeleton";

// Mutable reactivated props (not Immutable<>): this template composes the in-tree
// SiteHeader / SiteFooter chain via PageSkeleton, which is typed with mutable
// reactivated props. The body islands accept Immutable props, to which these are
// assignable.
type IProps = templates.HomePageTemplate;

const PRICE_STRIKE = "home-page-hero-content__copy-desc--strike";
const PRICE_ACTUAL =
    "home-page-hero-content__copy-desc--actuall-price home-page-hero-content__copy-highlight";

// The 4 price-copy variants: ``hero_description`` present/absent x
// ``retail == cosmetic``. The lead text is the ``hero_description`` (present) or
// ``"{pdp.title} starting at only"`` (absent); the strike-price arm inserts the
// retail price + screen-reader labels. Explicit ``{" "}`` preserves the visible
// spaces JSX would otherwise drop at line breaks.
const renderPriceCopy = (props: IProps): React.JSX.Element => {
    const { page } = props;
    const lead = page.hero_description ? (
        <>{page.hero_description}</>
    ) : (
        <>{props.hero_pdp_title} starting at only</>
    );
    const actual = (
        <span className={PRICE_ACTUAL}>{props.hero_price_cosmetic}</span>
    );
    if (props.hero_prices_equal) {
        return (
            <>
                {lead} {actual}
            </>
        );
    }
    return (
        <>
            {lead} <span className="screen-reader-only">Original price: </span>{" "}
            <span className={PRICE_STRIKE}>{props.hero_price_retail}</span>{" "}
            <span className="screen-reader-only">Current price: </span> {actual}
        </>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    // ``{% if %}`` guards are StreamValue truthiness (an empty stream is
    // falsy) for the StreamField-backed fields.
    const hasEmailSignup =
        !!page.email_signup && page.email_signup.value.length > 0;
    const hasHeroImageList =
        !!page.hero_image_list && page.hero_image_list.value.length > 0;
    const hasBody =
        !!page.home_page_props && page.home_page_props.value.length > 0;

    // GSAP "wipe" scroll animation for the stacked product-feature panels: while
    // the ``#scroll-trigger`` container is pinned and scrolled, each panel's
    // image fades out (``power2.in``) as the copy translates up and the next
    // panel's content fades in (``power4.in``), scrubbed 1:1 to scroll. It runs
    // only at/above the 768px desktop breakpoint (rebuilt / torn down on resize
    // across that breakpoint) and only when a product feature exists -- the
    // ``#scroll-trigger`` id (and thus the pin anchor) is rendered only then, so
    // the effect gates on ``product_feature_exists`` + the ref. gsap and
    // ScrollTrigger are dynamically imported INSIDE the effect so they never load
    // under SSR; the timeline and every created ScrollTrigger are killed on
    // cleanup.
    const scrollTriggerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!props.product_feature_exists) {
            return;
        }
        const triggerEl = scrollTriggerRef.current;
        if (!triggerEl) {
            return;
        }
        let cancelled = false;
        let teardown: (() => void) | null = null;
        void Promise.all([import("gsap"), import("gsap/ScrollTrigger")]).then(
            ([{ gsap }, { ScrollTrigger }]) => {
                if (cancelled) {
                    return;
                }
                gsap.registerPlugin(ScrollTrigger);
                // mobile breakpoint
                const MAX_WIDTH = 768;
                const wipeAnimation = gsap.timeline();
                const triggers: ReturnType<typeof ScrollTrigger.create>[] = [];
                let scrollActive = false;

                const initScrollTriggers = () => {
                    const document = getDocument();
                    if (!document) {
                        return;
                    }
                    const imageClips = document.querySelectorAll<HTMLElement>(
                        ".home-page-product-feature__image",
                    );
                    const copyClips = document.querySelectorAll<HTMLElement>(
                        ".home-page-product-feature__copy",
                    );
                    const contentClips = document.querySelectorAll<HTMLElement>(
                        ".home-page-product-feature__content",
                    );
                    const numChildren = imageClips.length;

                    const dist = -100 / numChildren;
                    let idx = numChildren;
                    let yStart = 0;
                    let yDest = dist;

                    while (idx--) {
                        const currentImageClip = imageClips[idx];
                        const currentContentClip = contentClips[idx];
                        const speed = 1;
                        if (idx !== 0) {
                            wipeAnimation.fromTo(
                                currentImageClip,
                                speed,
                                { autoAlpha: 1 },
                                {
                                    autoAlpha: 0,
                                    ease: "power2.in",
                                    delay: idx === numChildren - 1 ? 1 : 0,
                                },
                            );
                            wipeAnimation.fromTo(
                                copyClips,
                                speed,
                                { y: `${yStart}%` },
                                { y: `${yDest}%`, ease: "none", delay: 0 },
                                `-=${speed}`,
                            );
                            wipeAnimation.fromTo(
                                currentContentClip,
                                speed,
                                { autoAlpha: speed },
                                {
                                    autoAlpha: 1,
                                    ease: "power4.in",
                                    delay: 0,
                                },
                                `-=${speed}`,
                            );

                            yStart += dist;
                            yDest += dist;
                        }

                        scrollActive = true;
                    }

                    triggers.push(
                        ScrollTrigger.create({
                            trigger: triggerEl,
                            start: "top top",
                            end: "bottom bottom",
                            animation: wipeAnimation,
                            pin: true,
                            scrub: true,
                            toggleActions: "play none none reverse",
                        }),
                    );
                };

                const initialWindow = getWindow();
                if (initialWindow && initialWindow.innerWidth >= MAX_WIDTH) {
                    initScrollTriggers();
                }

                const resizeHandler = () => {
                    const window = getWindow();
                    if (!window) {
                        return;
                    }
                    const wWidth = window.innerWidth;
                    if (wWidth < MAX_WIDTH) {
                        triggers.forEach((trigger) => {
                            trigger.kill();
                        });
                        triggers.length = 0;
                        wipeAnimation.kill();
                        scrollActive = false;
                    } else {
                        if (scrollActive) {
                            return;
                        }
                        initScrollTriggers();
                    }
                };

                const listenerWindow = getWindow();
                listenerWindow?.addEventListener("resize", resizeHandler);

                teardown = () => {
                    listenerWindow?.removeEventListener(
                        "resize",
                        resizeHandler,
                    );
                    triggers.forEach((trigger) => {
                        trigger.kill();
                    });
                    wipeAnimation.kill();
                };
            },
        );
        return () => {
            cancelled = true;
            teardown?.();
        };
    }, [props.product_feature_exists]);

    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {hasEmailSignup ? <HomePageEmailSignup page={page} /> : null}

            <div className="l-full-width home-page-hero-container home-page-hero-height">
                {hasHeroImageList ? (
                    <div
                        className="home-page-hero-carousel"
                        role="region"
                        aria-label="homepage carousel"
                    >
                        <HomePageHeroCarousel page={page} />
                    </div>
                ) : null}

                {/* ALWAYS rendered: a missing mobile image yields a literal
                    empty ``url()`` (the side-channel defaults to ""). */}
                <div
                    className="home-page-hero-carousel__image home-page-hero-carousel__image--mobile"
                    style={{
                        backgroundImage: `url(${props.hero_mobile_image_url})`,
                    }}
                />

                {hasHeroImageList ? (
                    <>
                        {props.hero_promo_image_html !== null ? (
                            <div className="home-page-hero-promo">
                                <div className="home-page-hero-promo__ribbon">
                                    <div className="home-page-hero-promo__ribbon-inner">
                                        <div
                                            className="home-page-hero-promo__ribbon-itme"
                                            dangerouslySetInnerHTML={{
                                                __html: props.hero_promo_image_html,
                                            }}
                                        />
                                        <div className="home-page-hero-promo__ribbon-pre-title">
                                            {page.hero_promo_title}
                                        </div>
                                        <div className="home-page-hero-promo__ribbon-dash" />
                                        <div
                                            className="home-page-hero-promo__ribbon-copy"
                                            dangerouslySetInnerHTML={{
                                                __html: props.hero_promo_content,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <div className="home-page-hero-content">
                            <div className="home-page-hero-content__copy-container">
                                <div className="home-page-hero-content__copy">
                                    {page.hero_title ? (
                                        <div className="home-page-hero-content__copy-title">
                                            {page.hero_title}
                                        </div>
                                    ) : null}
                                    {props.hero_has_pdp ? (
                                        <>
                                            <div className="home-page-hero-content__copy-desc">
                                                {renderPriceCopy(props)}
                                            </div>
                                            <div
                                                className="home-page-hero-content__copy-sub-desc"
                                                dangerouslySetInnerHTML={{
                                                    __html: props.hero_offer_html,
                                                }}
                                            />
                                            <a
                                                className="button button--ming home-page-hero-content__button al-hp-hero__shop-now"
                                                href={
                                                    props.desktop_shop_now_url
                                                }
                                            >
                                                <span>SHOP NOW</span>
                                            </a>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>

            {props.hero_promo_content ? (
                <div className="l-capped-width home-page-hero-mobile-promo">
                    <div className="home-page-hero-mobile-promo__tagline">
                        {page.hero_promo_title}
                    </div>
                    <div
                        className="home-page-hero-mobile-promo__copy"
                        dangerouslySetInnerHTML={{
                            __html: props.hero_promo_content,
                        }}
                    />
                    {props.hero_has_pdp ? (
                        <a
                            className="button button--ming home-page-hero-mobile-promo__button"
                            href={props.mobile_shop_now_url}
                        >
                            <span>SHOP NOW</span>
                        </a>
                    ) : null}
                </div>
            ) : null}

            <div className="home-page-topper-feature-container">
                {/* ``#scroll-trigger`` is the GSAP pin anchor; the effect above
                    sets up / tears down its ScrollTrigger via this ref. */}
                <div
                    ref={scrollTriggerRef}
                    id={
                        props.product_feature_exists
                            ? "scroll-trigger"
                            : undefined
                    }
                >
                    {hasBody ? <HomePageBody page={page} /> : null}
                </div>
            </div>
        </PageSkeleton>
    );
};
